// Accepts youtube.com/watch?v=, youtu.be/, /embed/, /shorts/, and /live/ URLs.
export function getYouTubeVideoId(url: string | null | undefined): string | null {
  if (!url) return null
  const match = url.match(
    /(?:youtube(?:-nocookie)?\.com\/(?:watch\?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([\w-]{6,})/
  )
  return match?.[1] ?? null
}

// Returns a privacy-enhanced nocookie embed URL, or null if the URL isn't a
// recognized YouTube link.
export function getYouTubeEmbedUrl(
  url: string | null | undefined,
  { autoplay = true }: { autoplay?: boolean } = {}
): string | null {
  const videoId = getYouTubeVideoId(url)
  if (!videoId) return null
  // vq=hd1080 asks the player to start in HD; YouTube treats it as a hint and
  // still adapts to the viewer's bandwidth and player size.
  return `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=${autoplay ? 1 : 0}&rel=0&modestbranding=1&playsinline=1&vq=hd1080`
}

// Returns YouTube's hosted 1280x720 thumbnail for the video, or null if the
// URL isn't a recognized YouTube link. Pair the <img>/<Image> with
// handleYouTubeThumbnailFallback so videos without a maxres frame still show.
export function getYouTubeThumbnailUrl(url: string | null | undefined): string | null {
  const videoId = getYouTubeVideoId(url)
  return videoId ? `https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg` : null
}

// Swaps a missing maxres thumbnail for the always-available 480x360 one.
// YouTube answers a missing maxres with a 120x90 grey placeholder (so check
// naturalWidth on load) or, through the Next image optimizer, an error.
export function handleYouTubeThumbnailFallback(event: { currentTarget: HTMLImageElement }) {
  const img = event.currentTarget
  if (img.naturalWidth > 120) return
  if (!img.src.includes("maxresdefault")) return
  img.srcset = img.srcset.split("maxresdefault").join("hqdefault")
  img.src = img.src.split("maxresdefault").join("hqdefault")
}
